import * as THREE from "three";
import { seeded } from "./facet";
import { CUT_ATTRIBUTE, CUT_GROUPS, CUT_OWNER_STRIDE, Parts, type KitColour } from "./parts";

// The building kit (spike 002 C4; decision 0048: "a modular half-timbered kit in code (walls, timber frames, roofs,
// doors, fences, a well)"; the low-poly look of 0029). `createKit(defs, palette, material).build(piece, seed, tier)`
// turns a piece definition (the game's data) into:
//   object     a group with one vertex-coloured mesh on the given material (one draw call a piece, or none once
//              batched: `batchPieces` merges a town into one mesh);
//   colliders  descriptors for @rpg/collision, in the piece's frame (the game adds the layer: a ColliderSpec);
//   interior   for a building with a walkable room: its box, its door, the camera profile and light model the game
//              switches to inside, and its walls by cut group (so a look can cut away the roof and the near walls);
//   lods       a cheaper far form where it pays (houses: a block and a roof with its windows).
// Archetypes are mechanism (house, ruin, bridge, fence, well, stall, lantern, crate, barrel); every size, colour,
// count and chance is the game's, from its piece table. Variation comes from the seed (a range picks a value, a list
// picks an entry); the same piece, seed and tier give the same mesh. The tier drops detail (timber braces, window
// frames, shingle rows, small furniture, fence posts) on "low".

/** Quality tier: "low" drops small detail, "high" keeps it. */
export type KitTier = "low" | "high";
/** A number, or a [min, max] range the seed picks inside. */
export type Range = number | readonly [number, number];

export interface KitVec3 {
  x: number;
  y: number;
  z: number;
}
export interface KitQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** A collider shape (the same shapes and fields as @rpg/collision's primitives). */
export type KitShape = { type: "cuboid"; halfExtents: KitVec3 } | { type: "cylinder"; halfHeight: number; radius: number } | { type: "ball"; radius: number };

/** A collider in the piece's frame: shape, offset, rotation and surface tag. Add a `layer` and it is @rpg/collision's
 *  ColliderSpec. */
export interface ColliderDesc {
  shape: KitShape;
  offset: KitVec3;
  rotation?: KitQuat;
  tag?: string;
}

/** A building's walkable room, in the piece's frame. */
export interface InteriorDesc {
  /** The room inside the walls, floor to wall top. */
  bounds: { min: KitVec3; max: KitVec3 };
  /** The door: its centre on the wall's outer face, the outward normal, its size. */
  door: { x: number; y: number; z: number; nx: number; nz: number; width: number; height: number };
  /** The camera profile and the indoor light model the game uses inside (names from its data). */
  profile: string;
  light: string;
  /** Each cut-away wall: its cut group, outward normal and outer face's offset (n · p = d in the piece's frame). */
  walls: { group: number; nx: number; nz: number; d: number }[];
}

/** A built piece. */
export interface KitPiece {
  name: string;
  seed: number;
  tier: KitTier;
  /** The near form: a group holding one mesh (`kit:<name>`). */
  object: THREE.Group;
  /** The near form's geometry (position, normal, color, glow, lit, cut). */
  geometry: THREE.BufferGeometry;
  colliders: ColliderDesc[];
  interior?: InteriorDesc;
  /** Cheaper forms from a distance on (m), nearest first. */
  lods?: { distance: number; geometry: THREE.BufferGeometry; object: THREE.Group }[];
  /** The near form's triangles. */
  triangles: number;
}

// ── piece definitions (the game's data) ─────────────────────────────────────────────────────────────────

/** A piece of furniture inside a house: where in the room (u across x, v across z, −1..1), turned by `yaw`. */
export interface FurnitureDef {
  kind: "table" | "bench" | "bed" | "shelf" | "counter" | "forge" | "anvil" | "barrel" | "crate" | "hearth" | "rug";
  at: readonly [number, number];
  yaw?: number;
  /** Main colour (palette key); each kind has a sensible fallback from the house's own colours. */
  colour?: string;
  /** Second colour (a blanket, coals, goods). */
  accent?: string;
  /** Only on the high tier. */
  highOnly?: boolean;
}

export interface HouseDef {
  kind: "house";
  width: Range;
  depth: Range;
  wallHeight: Range;
  /** The plinth's height above the ground (the doorstep). */
  plinth: number;
  /** Wall colours: one is picked. */
  wall: readonly string[];
  stone: string;
  /** Timber frame colour (half-timbered), or omitted for plain walls. */
  timber?: string;
  roof: { pitch: Range; overhang: number; colours: readonly string[] };
  chimney?: { chance: number; colour: string };
  door: { width: number; height: number; colour: string };
  windows: { perSide: Range; width: number; height: number; frame: string; glass: string; litChance: number };
  /** An awning over the front, with posts (a shop, a smithy). */
  awning?: { depth: number; colours: readonly string[] };
  interior?: { profile: string; light: string; floor: string; furniture: readonly FurnitureDef[] };
  /** From this distance (m) on, the far form (default 60). */
  farDistance?: number;
}

export interface RuinDef {
  kind: "ruin";
  form: "wall" | "arch" | "column" | "gate";
  /** Wall length, arch or gate span (m). */
  length: Range;
  height: Range;
  thickness: number;
  stone: readonly string[];
  moss?: string;
  /** 0 whole .. 1 badly broken. */
  broken: number;
}

export interface BridgeDef {
  kind: "bridge";
  span: number;
  width: number;
  rise: number;
  deck: string;
  rail: string;
  stone: string;
}

export interface FenceDef {
  kind: "fence";
  length: number;
  height: number;
  /** Post spacing (m). */
  spacing: number;
  colour: string;
}

export interface WellDef {
  kind: "well";
  radius: number;
  height: number;
  stone: readonly string[];
  wood: string;
  roof: string;
  water: string;
}

export interface StallDef {
  kind: "stall";
  width: number;
  depth: number;
  wood: string;
  awning: readonly string[];
  goods: readonly string[];
}

export interface LanternDef {
  kind: "lantern";
  height: number;
  post: string;
  glass: string;
}

export interface PropDef {
  kind: "crate" | "barrel";
  colour: string;
  band?: string;
}

/** Any piece definition. */
export type PieceDef = HouseDef | RuinDef | BridgeDef | FenceDef | WellDef | StallDef | LanternDef | PropDef;

/** Planted defects (tests): `unseeded` draws variation from Math.random (the determinism check must go red);
 *  `noColliders` drops every collider (the collider checks must go red). */
export const KIT_FAULTS = { unseeded: false, noColliders: false };

// ── the kit ─────────────────────────────────────────────────────────────────────────────────────────

export interface KitOptions {
  /** Piece name → definition. */
  defs: Readonly<Record<string, PieceDef>>;
  /** Palette lookup (a key → a colour; @rpg/look's `createPalette`). */
  palette: (c: string) => THREE.Color;
  /** The material every piece draws with (one that reads glow, lit and cut: @rpg/look's kit surface). */
  material: THREE.Material;
  /** Per-face tint (default 0.05). */
  tint?: number;
}

export interface Kit {
  /** Build a piece. Throws on an unknown name. */
  build(piece: string, seed: number, tier: KitTier): KitPiece;
  /** The piece names. */
  names(): string[];
  def(piece: string): PieceDef;
}

/** A 32-bit hash of a string (FNV-1a). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The building kit over the game's piece definitions. */
export function createKit(o: KitOptions): Kit {
  const def = (piece: string): PieceDef => {
    const d = o.defs[piece];
    if (!d) throw new Error(`@rpg/meshkit kit: no piece "${piece}" (have ${Object.keys(o.defs).join(", ")})`);
    return d;
  };
  return {
    names: () => Object.keys(o.defs),
    def,
    build(piece, seed, tier) {
      const d = def(piece);
      const base = (hashString(piece) ^ Math.imul(seed | 0, 0x9e3779b1)) | 0;
      const rng = KIT_FAULTS.unseeded ? Math.random : seeded(base);
      const ctx = new Ctx(rng, seeded(base ^ 0x5bd1e995), o.palette, tier, o.tint ?? 0.05);
      const built = BUILDERS[d.kind](ctx, d as never);
      const geometry = ctx.parts.geometry();
      const object = wrap(piece, geometry, o.material);
      const out: KitPiece = { name: piece, seed, tier, object, geometry, colliders: KIT_FAULTS.noColliders ? [] : built.colliders, triangles: geometry.getAttribute("position").count / 3 };
      if (built.interior) out.interior = built.interior;
      if (built.far) {
        const fs = hashString(`${piece}:far`) ^ (seed | 0);
        const far = new Ctx(seeded(fs), seeded(fs ^ 0x5bd1e995), o.palette, "low", o.tint ?? 0.05);
        built.far.build(far);
        const g = far.parts.geometry();
        out.lods = [{ distance: built.far.distance, geometry: g, object: wrap(`${piece}:far`, g, o.material) }];
      }
      return out;
    },
  };
}

function wrap(name: string, geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  group.name = `kit:${name}`;
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `kit:${name}:mesh`;
  group.add(mesh);
  return group;
}

/** What a builder returns. */
interface Built {
  colliders: ColliderDesc[];
  interior?: InteriorDesc;
  far?: { distance: number; build(c: Ctx): void };
}

/**
 * A builder's context: the part sink, the seeded picks, the tier. Two random streams: `rng` decides the piece's shape
 * (sizes, openings, broken heights, which windows glow) and is drawn the same way on every tier, so a piece's
 * colliders and its far form never depend on the tier; `d` decides detail only (tints, jitter, moss, shelf goods,
 * rubble), so a tier's extra parts never shift the shape's draws.
 */
class Ctx {
  readonly parts: Parts;
  readonly hi: boolean;
  constructor(
    readonly rng: () => number,
    readonly d: () => number,
    palette: (c: string) => THREE.Color,
    readonly tier: KitTier,
    tint: number,
  ) {
    this.parts = new Parts(d, palette, tint);
    this.hi = tier === "high";
  }
  /** One of a list, from the detail stream. */
  dpick<T>(xs: readonly T[]): T {
    return xs[Math.min(xs.length - 1, Math.floor(this.d() * xs.length))]!;
  }
  /** A value from a range. */
  r(v: Range): number {
    return typeof v === "number" ? v : v[0] + (v[1] - v[0]) * this.rng();
  }
  /** A whole number from a range (inclusive). */
  ri(v: Range): number {
    return typeof v === "number" ? Math.round(v) : Math.floor(v[0] + (v[1] - v[0] + 1) * this.rng() - 1e-9);
  }
  /** One of a list. */
  pick<T>(xs: readonly T[]): T {
    return xs[Math.min(xs.length - 1, Math.floor(this.rng() * xs.length))]!;
  }
  /** True with this chance. */
  chance(p: number): boolean {
    return this.rng() < p;
  }
}

const yawQuat = (yaw: number): KitQuat => ({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) });
const pitchQuat = (a: number): KitQuat => ({ x: Math.sin(a / 2), y: 0, z: 0, w: Math.cos(a / 2) });
const cuboid = (hx: number, hy: number, hz: number, x: number, y: number, z: number, tag?: string, rotation?: KitQuat): ColliderDesc => ({
  shape: { type: "cuboid", halfExtents: { x: hx, y: hy, z: hz } },
  offset: { x, y, z },
  ...(rotation ? { rotation } : {}),
  ...(tag ? { tag } : {}),
});
const cylinder = (r: number, hh: number, x: number, y: number, z: number, tag?: string): ColliderDesc => ({ shape: { type: "cylinder", halfHeight: hh, radius: r }, offset: { x, y, z }, ...(tag ? { tag } : {}) });

/** Move colliders built in a sub-frame (at x, y, z turned by yaw) into the parent frame. */
function placeColliders(cs: ColliderDesc[], x: number, y: number, z: number, yaw: number): ColliderDesc[] {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
  return cs.map((d) => {
    const o = d.offset;
    const rot = d.rotation ? q.clone().multiply(new THREE.Quaternion(d.rotation.x, d.rotation.y, d.rotation.z, d.rotation.w)) : q;
    const out: ColliderDesc = { ...d, offset: { x: x + o.x * c + o.z * s, y: y + o.y, z: z - o.x * s + o.z * c } };
    if (yaw !== 0 || d.rotation) out.rotation = { x: rot.x, y: rot.y, z: rot.z, w: rot.w };
    return out;
  });
}

// ── house ──────────────────────────────────────────────────────────────────────────────────────────

/** One wall's frame: centre of its outer face (on the floor line), the yaw that turns +z outward, its length, group. */
interface WallFrame {
  x: number;
  z: number;
  yaw: number;
  len: number;
  group: number;
  /** Openings along the wall (centre, width) the trim skips: the door. */
  gaps: { at: number; w: number; h: number }[];
  windows: number[];
  /** Whether each window glows at night (1) or not (0): picked once, shared by the near and far forms. */
  lit: number[];
}

function house(c: Ctx, d: HouseDef): Built {
  const p = c.parts;
  const W = c.r(d.width);
  const D = c.r(d.depth);
  const H = c.r(d.wallHeight);
  const P = d.plinth;
  const T = 0.22;
  const wallCol = c.pick(d.wall);
  const roofCol = c.pick(d.roof.colours);
  const pitch = c.r(d.roof.pitch);
  const o = d.roof.overhang;
  const dw = d.door.width;
  const dh = d.door.height;
  // the door: a seeded offset along the front, kept clear of the corners
  const doorX = Math.round((c.rng() * 2 - 1) * Math.max(0, W / 2 - dw / 2 - 0.9) * 10) / 10;
  const colliders: ColliderDesc[] = [];
  const stoneTag = "stone";

  // plinth (down into the ground so a slope never shows under it) and the floor
  p.box(W + 0.2, P + 0.6, D + 0.2, 0, (P - 0.6) / 2, 0, d.stone, { jitter: 0.015 });
  colliders.push(cuboid(W / 2 + 0.1, (P + 0.6) / 2, D / 2 + 0.1, 0, (P - 0.6) / 2, 0, stoneTag));
  p.box(dw + 0.5, 0.1, 0.45, doorX, P / 2 - 0.05, D / 2 + 0.32, d.stone);
  colliders.push(cuboid(dw / 2 + 0.25, P / 4, 0.22, doorX, P / 4, D / 2 + 0.32, stoneTag));
  const floorCol = d.interior?.floor ?? d.stone;
  const planks = c.hi ? Math.max(3, Math.round((W - 2 * T) / 0.45)) : 1;
  for (let i = 0; i < planks; i++) {
    const w = (W - 2 * T) / planks;
    p.box(w * 0.98, 0.04, D - 2 * T, -W / 2 + T + w * (i + 0.5), P + 0.02, 0, floorCol, { tint: 0.08 });
  }

  // windows per side, kept clear of the door
  const ww = d.windows.width;
  const wh = d.windows.height;
  const wy = P + Math.min(H - wh / 2 - 0.35, 1.45);
  const sideWindows = (len: number, gaps: { at: number; w: number }[]): number[] => {
    const n = c.ri(d.windows.perSide);
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
      const t = ((i + 0.5) / n - 0.5) * (len - 0.8);
      if (gaps.some((g) => Math.abs(t - g.at) < g.w / 2 + ww / 2 + 0.25)) continue;
      out.push(t);
    }
    return out;
  };
  const doorGap = { at: doorX, w: dw, h: dh };
  const walls: WallFrame[] = [
    { x: 0, z: D / 2, yaw: 0, len: W, group: CUT_GROUPS.pz, gaps: [doorGap], windows: [], lit: [] },
    { x: 0, z: -D / 2, yaw: Math.PI, len: W, group: CUT_GROUPS.nz, gaps: [], windows: [], lit: [] },
    { x: W / 2, z: 0, yaw: Math.PI / 2, len: D - 2 * T, group: CUT_GROUPS.px, gaps: [], windows: [], lit: [] },
    { x: -W / 2, z: 0, yaw: -Math.PI / 2, len: D - 2 * T, group: CUT_GROUPS.nx, gaps: [], windows: [], lit: [] },
  ];
  // the door's x in the front wall's frame is doorX (yaw 0)
  for (const w of walls) {
    w.windows = sideWindows(w.len, w.gaps);
    w.lit = w.windows.map(() => (c.chance(d.windows.litChance) ? 1 : 0));
  }

  for (const w of walls) {
    const cut = { cut: w.group };
    p.at(w.x, 0, w.z, w.yaw, () => {
      // the wall slab, split round its openings (only the door is a real opening); its colliders in the house frame
      const solid: [number, number][] = [];
      let from = -w.len / 2;
      for (const g of [...w.gaps].sort((a, b) => a.at - b.at)) {
        solid.push([from, g.at - g.w / 2]);
        from = g.at + g.w / 2;
        // the lintel above the opening
        p.box(g.w, H - g.h, T, g.at, P + g.h + (H - g.h) / 2, -T / 2, wallCol, cut);
      }
      solid.push([from, w.len / 2]);
      for (const [a, b] of solid) if (b - a > 0.01) p.box(b - a, H, T, (a + b) / 2, P + H / 2, -T / 2, wallCol, cut);
      // the glass spans the wall, so it shows inside and out; lit by chance at night
      w.windows.forEach((t, k) => {
        const lit = w.lit[k]!;
        p.box(ww, wh, T + 0.04, t, wy, -T / 2, d.windows.glass, { ...cut, lit, tint: 0.02 });
        if (c.hi) {
          const fr = d.windows.frame;
          p.box(ww + 0.16, 0.08, 0.06, t, wy - wh / 2 - 0.04, 0.035, fr, cut);
          p.box(ww + 0.16, 0.08, 0.06, t, wy + wh / 2 + 0.04, 0.035, fr, cut);
          p.box(0.08, wh, 0.06, t - ww / 2 - 0.04, wy, 0.035, fr, cut);
          p.box(0.08, wh, 0.06, t + ww / 2 + 0.04, wy, 0.035, fr, cut);
          p.box(0.05, wh, 0.05, t, wy, 0.035, fr, cut);
          p.box(ww + 0.24, 0.06, 0.16, t, wy - wh / 2 - 0.1, 0.085, d.stone, cut);
        }
      });
      // the half-timbering on the outer face
      if (d.timber) {
        const tb = d.timber;
        const z = 0.04;
        const rail = (y: number): void => {
          let a = -w.len / 2;
          for (const g of w.gaps) {
            if (y < P + g.h) {
              if (g.at - g.w / 2 - a > 0.05) p.box(g.at - g.w / 2 - a, 0.12, 0.07, (a + g.at - g.w / 2) / 2, y, z, tb, cut);
              a = g.at + g.w / 2;
            }
          }
          if (w.len / 2 - a > 0.05) p.box(w.len / 2 - a, 0.12, 0.07, (a + w.len / 2) / 2, y, z, tb, cut);
        };
        rail(P + 0.07);
        rail(P + H - 0.07);
        if (c.hi) rail(wy - wh / 2 - 0.16);
        for (const sx of [-1, 1]) p.box(0.14, H, 0.08, sx * (w.len / 2 - 0.07), P + H / 2, z, tb, cut);
        if (c.hi) {
          // studs every ~1.4 m clear of the windows and the door, and a brace at each end of the lower panel
          const n = Math.max(0, Math.floor(w.len / 1.4) - 1);
          for (let i = 1; i <= n; i++) {
            const t = -w.len / 2 + (i * w.len) / (n + 1);
            if (w.windows.some((x) => Math.abs(t - x) < ww / 2 + 0.15) || w.gaps.some((g) => Math.abs(t - g.at) < g.w / 2 + 0.15)) continue;
            p.box(0.1, H - 0.14, 0.07, t, P + H / 2, z, tb, cut);
          }
          const lowH = wy - wh / 2 - 0.16 - P;
          for (const sx of [-1, 1]) {
            const x0 = sx * (w.len / 2 - 0.14);
            const x1 = sx * (w.len / 2 - 0.14 - lowH * 0.8);
            if (w.gaps.some((g) => Math.abs((x0 + x1) / 2 - g.at) < g.w / 2 + 0.4)) continue;
            const len = Math.hypot(x1 - x0, lowH);
            p.box(0.1, len, 0.06, (x0 + x1) / 2, P + lowH / 2, z, tb, cut, 0, 0, Math.atan2(x1 - x0, lowH));
          }
        }
      }
    });
    // colliders for the wall's solid parts, in the house frame
    const cs = Math.cos(w.yaw);
    const sn = Math.sin(w.yaw);
    let from = -w.len / 2;
    const segs: [number, number][] = [];
    for (const g of [...w.gaps].sort((a, b) => a.at - b.at)) {
      segs.push([from, g.at - g.w / 2]);
      from = g.at + g.w / 2;
      // the lintel
      const mx = g.at;
      colliders.push({ ...cuboid(g.w / 2, (H - g.h) / 2, T / 2, w.x + mx * cs + -T / 2 * sn, P + g.h + (H - g.h) / 2, w.z - mx * sn + -T / 2 * cs, "wood"), ...(w.yaw ? { rotation: yawQuat(w.yaw) } : {}) });
    }
    segs.push([from, w.len / 2]);
    for (const [a, b] of segs) {
      if (b - a <= 0.01) continue;
      const mx = (a + b) / 2;
      colliders.push({ ...cuboid((b - a) / 2, H / 2, T / 2, w.x + mx * cs + -T / 2 * sn, P + H / 2, w.z - mx * sn + -T / 2 * cs, "wood"), ...(w.yaw ? { rotation: yawQuat(w.yaw) } : {}) });
    }
  }

  // the door leaf, swung open against the inside of the front wall
  p.box(0.06, dh * 0.97, dw * 0.92, doorX - dw / 2 + 0.06, P + dh / 2, D / 2 - T - dw * 0.46, d.door.colour, { cut: CUT_GROUPS.pz });
  if (c.hi) {
    p.box(dw + 0.2, 0.12, 0.08, doorX, P + dh + 0.06, D / 2 + 0.045, d.timber ?? d.door.colour, { cut: CUT_GROUPS.pz });
    for (const sx of [-1, 1]) p.box(0.1, dh, 0.08, doorX + sx * (dw / 2 + 0.05), P + dh / 2, D / 2 + 0.045, d.timber ?? d.door.colour, { cut: CUT_GROUPS.pz });
  }

  // the roof: a gable along x; slabs (shingle rows on high), gables, ridge, maybe a chimney
  const top = P + H;
  const rise = (D / 2) * Math.tan(pitch);
  const ridgeY = top + rise;
  const roofCut = { cut: CUT_GROUPS.roof };
  const slope = (D / 2 + o) / Math.cos(pitch);
  const rows = c.hi ? 4 : 1;
  for (const sz of [1, -1]) {
    for (let i = 0; i < rows; i++) {
      const s0 = (i / rows) * slope;
      const s1 = ((i + 1) / rows) * slope;
      const sm = (s0 + s1) / 2;
      const len = (s1 - s0) * (rows > 1 ? 1.06 : 1);
      // along the slope from the ridge: (0, −sin a, ±cos a); lifted by half the slab along its normal
      const y = ridgeY - Math.sin(pitch) * sm + Math.cos(pitch) * 0.06 + (rows > 1 ? (i % 2) * 0.012 : 0);
      const z = sz * (Math.cos(pitch) * sm + Math.sin(pitch) * 0.06);
      p.box(W + 2 * o, 0.1, len, 0, y, z, roofCol, { ...roofCut, tint: 0.06 }, sz * pitch);
    }
  }
  const gable: [number, number][] = [
    [-D / 2, 0],
    [D / 2, 0],
    [0, rise],
  ];
  for (const sx of [1, -1]) p.prism(gable, T, wallCol, roofCut, new THREE.Matrix4().makeTranslation(sx * (W / 2 - T / 2), top, 0).multiply(new THREE.Matrix4().makeRotationY(Math.PI / 2)));
  if (c.hi) p.box(W + 2 * o + 0.1, 0.14, 0.18, 0, ridgeY + 0.1, 0, d.timber ?? roofCol, roofCut);
  if (d.chimney && c.chance(d.chimney.chance)) {
    const cx = (c.chance(0.5) ? 1 : -1) * (W / 2 - 0.7);
    const cz = -D / 4;
    const base = top;
    const h = ridgeY + 0.6 - base;
    p.box(0.55, h, 0.55, cx, base + h / 2, cz, d.chimney.colour, { ...roofCut, jitter: 0.01 });
    if (c.hi) p.box(0.7, 0.12, 0.7, cx, base + h + 0.06, cz, d.chimney.colour, roofCut);
  }

  // an awning over the front with two posts
  if (d.awning) {
    const aw = Math.min(W - 0.4, 3.2);
    const ad = d.awning.depth;
    const y0 = P + Math.min(H - 0.15, dh + 0.45);
    const drop = 0.35;
    const strips = c.hi ? 5 : 1;
    const ang = Math.atan2(drop, ad);
    for (let i = 0; i < strips; i++) {
      const sw = aw / strips;
      p.box(sw, 0.05, Math.hypot(ad, drop), -aw / 2 + sw * (i + 0.5), y0 - drop / 2, D / 2 + ad / 2, d.awning.colours[i % d.awning.colours.length]!, { cut: CUT_GROUPS.pz }, ang);
    }
    for (const sx of [-1, 1]) p.box(0.1, y0 - drop, 0.1, sx * (aw / 2 - 0.05), (y0 - drop) / 2, D / 2 + ad - 0.05, d.timber ?? d.door.colour, { cut: CUT_GROUPS.pz });
    for (const sx of [-1, 1]) colliders.push(cylinder(0.08, (y0 - drop) / 2, sx * (aw / 2 - 0.05), (y0 - drop) / 2, D / 2 + ad - 0.05, "wood"));
  }

  // the room and its furniture
  let interior: InteriorDesc | undefined;
  if (d.interior) {
    const hx = W / 2 - T;
    const hz = D / 2 - T;
    for (const f of d.interior.furniture) {
      // detail furniture shows on the high tier only, and never blocks (colliders never depend on the tier)
      if (f.highOnly && !c.hi) continue;
      const fx = f.at[0] * Math.max(0, hx - 0.65);
      const fz = f.at[1] * Math.max(0, hz - 0.65);
      const yaw = f.yaw ?? 0;
      let local: ColliderDesc[] = [];
      p.at(fx, P + 0.04, fz, yaw, () => {
        local = furniture(c, f, { wall: wallCol, wood: d.timber ?? d.door.colour, stone: d.stone, roof: roofCol });
      });
      if (!f.highOnly) colliders.push(...placeColliders(local, fx, P + 0.04, fz, yaw));
    }
    interior = {
      // the room from just under the floor (the feet stand on it) to the wall top
      bounds: { min: { x: -hx, y: P - 0.25, z: -hz }, max: { x: hx, y: top, z: hz } },
      door: { x: doorX, y: P, z: D / 2, nx: 0, nz: 1, width: dw, height: dh },
      profile: d.interior.profile,
      light: d.interior.light,
      walls: [
        { group: CUT_GROUPS.pz, nx: 0, nz: 1, d: D / 2 },
        { group: CUT_GROUPS.nz, nx: 0, nz: -1, d: D / 2 },
        { group: CUT_GROUPS.px, nx: 1, nz: 0, d: W / 2 },
        { group: CUT_GROUPS.nx, nx: -1, nz: 0, d: W / 2 },
      ],
    };
  }

  // the far form: the block, the roof and the windows (lit as near), no trim, no room
  const far = {
    distance: d.farDistance ?? 60,
    build(f: Ctx): void {
      const q = f.parts;
      q.box(W, H + P, D, 0, (H + P) / 2, 0, wallCol);
      for (const sz of [1, -1]) {
        const sm = slope / 2;
        q.box(W + 2 * o, 0.1, slope, 0, ridgeY - Math.sin(pitch) * sm + Math.cos(pitch) * 0.06, sz * (Math.cos(pitch) * sm + Math.sin(pitch) * 0.06), roofCol, {}, sz * pitch);
      }
      for (const sx of [1, -1]) q.prism(gable, 0.1, wallCol, {}, new THREE.Matrix4().makeTranslation(sx * (W / 2 - 0.05), top, 0).multiply(new THREE.Matrix4().makeRotationY(Math.PI / 2)));
      walls.forEach((w, i) =>
        q.at(w.x, 0, w.z, w.yaw, () => {
          w.windows.forEach((t, k) => q.box(ww, wh, 0.04, t, wy, 0.01, d.windows.glass, { lit: walls[i]!.lit[k]! }));
        }),
      );
    },
  };
  return { colliders, ...(interior ? { interior } : {}), far };
}

// ── furniture ──────────────────────────────────────────────────────────────────────────────────────

function furniture(c: Ctx, f: FurnitureDef, k: { wall: string; wood: string; stone: string; roof: string }): ColliderDesc[] {
  const p = c.parts;
  const wood = f.colour ?? k.wood;
  switch (f.kind) {
    case "table":
      p.box(1.3, 0.08, 0.8, 0, 0.74, 0, wood);
      if (c.hi) for (const [x, z] of [[-0.55, -0.32], [0.55, -0.32], [-0.55, 0.32], [0.55, 0.32]] as const) p.box(0.08, 0.7, 0.08, x, 0.35, z, wood);
      else p.box(0.3, 0.7, 0.3, 0, 0.35, 0, wood);
      if (f.accent) p.box(0.3, 0.12, 0.3, 0.2, 0.84, 0.05, f.accent);
      return [cuboid(0.65, 0.4, 0.4, 0, 0.4, 0, "wood")];
    case "bench":
      p.box(1.3, 0.07, 0.32, 0, 0.45, 0, wood);
      for (const sx of [-1, 1]) p.box(0.08, 0.42, 0.28, sx * 0.55, 0.21, 0, wood);
      return [cuboid(0.65, 0.25, 0.16, 0, 0.25, 0, "wood")];
    case "bed":
      p.box(1.05, 0.35, 2.0, 0, 0.18, 0, wood);
      p.box(0.98, 0.12, 1.4, 0, 0.41, 0.25, f.accent ?? k.roof, { tint: 0.04 });
      p.box(0.7, 0.12, 0.35, 0, 0.42, -0.72, f.colour ? k.wall : "#f2ead8");
      if (c.hi) p.box(1.05, 0.8, 0.08, 0, 0.4, -0.98, wood);
      return [cuboid(0.53, 0.25, 1.0, 0, 0.25, 0, "wood")];
    case "shelf": {
      p.box(1.3, 1.9, 0.38, 0, 0.95, 0, wood);
      if (c.hi)
        for (let r = 0; r < 3; r++)
          for (let i = 0; i < 4; i++) if (c.d() < 0.75) p.box(0.18, 0.22 + c.d() * 0.1, 0.22, -0.45 + i * 0.3, 0.55 + r * 0.55, 0.12, c.dpick([f.accent ?? k.roof, k.stone, k.wall]));
      return [cuboid(0.65, 0.95, 0.2, 0, 0.95, 0, "wood")];
    }
    case "counter":
      p.box(2.2, 0.95, 0.65, 0, 0.475, 0, wood);
      p.box(2.3, 0.07, 0.75, 0, 0.98, 0, f.accent ?? k.stone);
      if (c.hi) for (let i = 0; i < 3; i++) p.box(0.25, 0.2, 0.25, -0.7 + i * 0.6, 1.11, 0, c.dpick([k.roof, k.wall, wood]));
      return [cuboid(1.12, 0.5, 0.36, 0, 0.5, 0, "wood")];
    case "forge":
      p.box(1.4, 0.9, 1.1, 0, 0.45, 0, f.colour ?? k.stone, { jitter: 0.02 });
      p.box(0.9, 0.08, 0.65, 0, 0.92, 0, f.accent ?? "#ff8a2a", { glow: 1, tint: 0.15 });
      if (c.hi) p.box(1.0, 0.12, 0.75, 0, 0.86, 0, "#2a2420");
      return [cuboid(0.7, 0.5, 0.55, 0, 0.5, 0, "stone")];
    case "anvil":
      p.cyl(0.28, 0.32, 0.5, c.hi ? 8 : 6, 0, 0, 0, k.wood);
      p.box(0.6, 0.18, 0.25, 0, 0.6, 0, f.colour ?? "#4a4a50");
      if (c.hi) p.box(0.22, 0.1, 0.18, 0.36, 0.63, 0, f.colour ?? "#4a4a50");
      return [cylinder(0.33, 0.35, 0, 0.35, 0, "metal")];
    case "barrel":
      p.cyl(0.32, 0.28, 0.85, c.hi ? 10 : 7, 0, 0, 0, wood);
      if (c.hi) for (const y of [0.15, 0.68]) p.cyl(0.335, 0.335, 0.05, 10, 0, y, 0, f.accent ?? "#3a3430");
      return [cylinder(0.33, 0.43, 0, 0.43, 0, "wood")];
    case "crate":
      p.box(0.7, 0.7, 0.7, 0, 0.35, 0, wood);
      if (c.hi) for (const sx of [-1, 1]) p.box(0.08, 0.72, 0.72, sx * 0.32, 0.35, 0, f.accent ?? k.wood);
      return [cuboid(0.36, 0.36, 0.36, 0, 0.36, 0, "wood")];
    case "hearth":
      p.box(1.5, 1.2, 0.7, 0, 0.6, 0, f.colour ?? k.stone, { jitter: 0.02 });
      p.box(0.8, 0.5, 0.2, 0, 0.35, 0.3, f.accent ?? "#ff9a3a", { glow: 1, tint: 0.15 });
      return [cuboid(0.75, 0.6, 0.35, 0, 0.6, 0, "stone")];
    case "rug":
      p.box(1.6, 0.02, 1.1, 0, 0.01, 0, f.colour ?? k.roof, { tint: 0.03 });
      if (c.hi) p.box(1.3, 0.025, 0.8, 0, 0.012, 0, f.accent ?? k.wall, { tint: 0.03 });
      return [];
  }
}

// ── ruins ──────────────────────────────────────────────────────────────────────────────────────────

function ruin(c: Ctx, d: RuinDef): Built {
  const p = c.parts;
  const L = c.r(d.length);
  const H = c.r(d.height);
  const T = d.thickness;
  const stone = (): string => c.dpick(d.stone);
  const rough = { jitter: 0.03, tint: 0.07 };
  const colliders: ColliderDesc[] = [];
  const course = c.hi ? 0.42 : 0.7;
  /** A wall of stone courses from x0 to x1, its top broken by column. */
  const wall = (x0: number, x1: number, h: number, t: number): void => {
    const cols = Math.max(1, Math.round((x1 - x0) / 1.0));
    const cw = (x1 - x0) / cols;
    for (let i = 0; i < cols; i++) {
      const top = h * (1 - d.broken * c.rng() * (i === 0 || i === cols - 1 ? 0.4 : 1));
      const rowsN = Math.max(1, Math.round(top / course));
      const rh = top / rowsN;
      for (let r = 0; r < rowsN; r++) {
        // running bond: alternate rows split the column's block in two
        const split = c.hi && r % 2 === 1;
        const xa = x0 + cw * i;
        if (split) {
          p.box(cw / 2 - 0.02, rh - 0.03, t, xa + cw / 4, rh * (r + 0.5), 0, stone(), rough);
          p.box(cw / 2 - 0.02, rh - 0.03, t, xa + (3 * cw) / 4, rh * (r + 0.5), 0, stone(), rough);
        } else p.box(cw - 0.03, rh - 0.03, t, xa + cw / 2, rh * (r + 0.5), 0, stone(), rough);
      }
      if (d.moss && c.hi && c.d() < 0.6) p.box(cw * 0.8, 0.06, t * 0.9, x0 + cw * (i + 0.5), top + 0.02, 0, d.moss, { jitter: 0.02, tint: 0.1 });
      colliders.push(cuboid(cw / 2, top / 2, t / 2, x0 + cw * (i + 0.5), top / 2, 0, "stone"));
    }
  };
  const pillar = (x: number, h: number, w: number): void => {
    const n = Math.max(1, Math.round(h / course));
    for (let r = 0; r < n; r++) p.box(w - 0.02 * (r % 2), h / n - 0.03, w - 0.02 * ((r + 1) % 2), x, (h / n) * (r + 0.5), 0, stone(), rough);
    colliders.push(cuboid(w / 2, h / 2, w / 2, x, h / 2, 0, "stone"));
  };
  if (d.form === "wall") wall(-L / 2, L / 2, H, T);
  else if (d.form === "column") {
    const r = Math.max(0.25, T / 2);
    const seg = c.hi ? 9 : 6;
    p.box(r * 2.6, 0.3, r * 2.6, 0, 0.15, 0, stone(), rough);
    const drums = Math.max(2, Math.round(H / 0.8));
    const keep = Math.max(1, Math.round(drums * (1 - d.broken * c.rng() * 0.7)));
    for (let i = 0; i < keep; i++) p.cyl(r * 0.97, r, H / drums - 0.03, seg, 0, 0.3 + (H / drums) * i, 0, stone(), rough);
    const h = 0.3 + (H / drums) * keep;
    if (keep === drums && c.hi) p.box(r * 2.4, 0.25, r * 2.4, 0, h + 0.12, 0, stone(), rough);
    colliders.push(cylinder(r * 1.3, h / 2, 0, h / 2, 0, "stone"));
  } else {
    // an arch or a gate: two pillars and what spans them
    const w = Math.max(0.6, T * 1.2);
    const span = L;
    pillar(-span / 2 - w / 2, H, w);
    pillar(span / 2 + w / 2, H, w);
    if (d.form === "arch") {
      const R = span / 2;
      const n = c.hi ? 9 : 5;
      for (let i = 0; i < n; i++) {
        if (i !== Math.floor(n / 2) && c.d() < d.broken * 0.35) continue; // a fallen voussoir (never the keystone)
        const a = Math.PI * ((i + 0.5) / n);
        const x = -Math.cos(a) * (R + w / 2);
        const y = H + Math.sin(a) * (R + w / 2);
        p.box((Math.PI * (R + w / 2)) / n - 0.04, w, T, x, y, 0, stone(), rough, 0, 0, Math.PI / 2 - a);
      }
      colliders.push(cuboid(span / 2 + w, w / 2, T / 2, 0, H + R + w / 2, 0, "stone"));
    } else {
      const lh = 0.6;
      p.box(span + 2 * w + 0.4, lh, T + 0.1, 0, H + lh / 2, 0, stone(), rough);
      if (c.hi) p.box(span + 2 * w + 0.6, 0.2, T + 0.25, 0, H + lh + 0.1, 0, stone(), rough);
      colliders.push(cuboid(span / 2 + w + 0.2, lh / 2, (T + 0.1) / 2, 0, H + lh / 2, 0, "stone"));
      // stubs of wall either side
      const stub = Math.max(1, L * 0.6);
      wall(-span / 2 - w - stub, -span / 2 - w, H * 0.6, T * 0.8);
      wall(span / 2 + w, span / 2 + w + stub, H * 0.6, T * 0.8);
    }
  }
  // rubble at the foot
  const rubble = c.hi ? 6 : 2;
  for (let i = 0; i < rubble * d.broken + (d.broken > 0 ? 1 : 0); i++) {
    const s = 0.2 + c.d() * 0.3;
    p.box(s, s * 0.7, s, (c.d() * 2 - 1) * (L / 2 + 0.5), s * 0.3, (c.d() < 0.5 ? -1 : 1) * (T / 2 + 0.3 + c.d() * 0.5), stone(), { jitter: 0.05, tint: 0.08 }, c.d(), c.d(), 0);
  }
  return { colliders };
}

// ── bridge, fence, well, stall, lantern, props ────────────────────────────────────────────────────

function bridge(c: Ctx, d: BridgeDef): Built {
  const p = c.parts;
  const S = d.span;
  const Wd = d.width;
  const n = c.hi ? 10 : 5;
  const yAt = (z: number): number => d.rise * (1 - ((2 * z) / S) ** 2);
  const colliders: ColliderDesc[] = [];
  const reach = (i: number, m: number) => {
    const z0 = -S / 2 + (S * i) / m;
    const z1 = -S / 2 + (S * (i + 1)) / m;
    const y0 = yAt(z0);
    const y1 = yAt(z1);
    return { z0, y0, len: Math.hypot(z1 - z0, y1 - y0), ang: -Math.atan2(y1 - y0, z1 - z0), zm: (z0 + z1) / 2, ym: (y0 + y1) / 2 };
  };
  for (let i = 0; i < n; i++) {
    const { z0, y0, len, ang, zm, ym } = reach(i, n);
    p.box(Wd, 0.14, len * 1.02, 0, ym - 0.07, zm, i % 2 ? d.deck : d.rail, { tint: 0.06 }, ang);
    // the rails: posts and a top rail
    for (const sx of [-1, 1]) {
      if (c.hi || i % 2 === 0) p.box(0.1, 0.9, 0.1, sx * (Wd / 2 - 0.05), y0 + 0.45, z0, d.rail);
      p.box(0.08, 0.08, len * 1.02, sx * (Wd / 2 - 0.05), ym + 0.86, zm, d.rail, {}, ang);
    }
  }
  // the colliders on a fixed count of reaches (never the tier's): the deck, and a solid rail either side
  const CN = 8;
  for (let i = 0; i < CN; i++) {
    const { len, ang, zm, ym } = reach(i, CN);
    colliders.push(cuboid(Wd / 2, 0.1, (len * 1.03) / 2, 0, ym - 0.1, zm, "wood", pitchQuat(ang)));
    for (const sx of [-1, 1]) colliders.push(cuboid(0.06, 0.5, (len * 1.03) / 2, sx * (Wd / 2 - 0.05), ym + 0.5, zm, "wood", pitchQuat(ang)));
  }
  for (const sx of [-1, 1]) p.box(0.1, 0.9, 0.1, sx * (Wd / 2 - 0.05), 0.45, S / 2, d.rail);
  // stone abutments at the ends, and beams under the arch on high
  for (const sz of [-1, 1]) p.box(Wd + 0.4, 0.8, 0.8, 0, -0.38, sz * (S / 2 + 0.2), d.stone, { jitter: 0.02 });
  if (c.hi) for (const sx of [-1, 1]) for (let i = 0; i < n; i++) {
    const z0 = -S / 2 + (S * i) / n;
    const z1 = -S / 2 + (S * (i + 1)) / n;
    const ang = -Math.atan2(yAt(z1) - yAt(z0), z1 - z0);
    p.box(0.14, 0.2, Math.hypot(z1 - z0, yAt(z1) - yAt(z0)), sx * (Wd / 2 - 0.25), (yAt(z0) + yAt(z1)) / 2 - 0.22, (z0 + z1) / 2, d.rail, {}, ang);
  }
  return { colliders };
}

function fence(c: Ctx, d: FenceDef): Built {
  const p = c.parts;
  const L = d.length;
  const step = c.hi ? d.spacing : d.spacing * 2;
  const n = Math.max(1, Math.round(L / step));
  for (let i = 0; i <= n; i++) p.box(0.12, d.height, 0.12, -L / 2 + (L * i) / n, d.height / 2, 0, d.colour, { jitter: 0.01 });
  const rails = c.hi ? [0.35, 0.75] : [0.6];
  for (const r of rails) p.box(L, 0.08, 0.06, 0, d.height * r, 0.06, d.colour);
  return { colliders: [cuboid(L / 2, 0.55, 0.08, 0, 0.55, 0, "wood")] };
}

function well(c: Ctx, d: WellDef): Built {
  const p = c.parts;
  const R = d.radius;
  const n = c.hi ? 10 : 7;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const seg = 2 * R * Math.sin(Math.PI / n) + 0.05;
    p.at(Math.sin(a) * R, 0, Math.cos(a) * R, a, () => p.box(seg, 0.8, 0.3, 0, 0.4, 0, c.dpick(d.stone), { jitter: 0.02, tint: 0.07 }));
  }
  p.cyl(R - 0.1, R - 0.1, 0.05, n, 0, 0.45, 0, d.water, { tint: 0.02 });
  for (const sx of [-1, 1]) p.box(0.14, 1.9, 0.14, sx * (R + 0.05), 0.95, 0, d.wood);
  p.box(2 * R + 0.4, 0.1, 0.1, 0, 1.75, 0, d.wood);
  const roof: [number, number][] = [
    [-R - 0.45, 0],
    [R + 0.45, 0],
    [0, 0.6],
  ];
  p.prism(roof, 1.2, d.roof, {}, new THREE.Matrix4().makeTranslation(0, 1.9, 0));
  if (c.hi) {
    p.cyl(0.14, 0.12, 0.25, 7, 0.1, 1.2, 0, d.wood);
    p.box(0.02, 0.5, 0.02, 0.1, 1.5, 0, "#3a3430");
  }
  return { colliders: [cylinder(R + 0.15, 0.4, 0, 0.4, 0, "stone"), cylinder(0.08, 0.95, R + 0.05, 0.95, 0, "wood"), cylinder(0.08, 0.95, -R - 0.05, 0.95, 0, "wood")] };
}

function stall(c: Ctx, d: StallDef): Built {
  const p = c.parts;
  const W = d.width;
  const D = d.depth;
  p.box(W, 0.08, D, 0, 0.9, 0, d.wood);
  p.box(W - 0.1, 0.8, 0.06, 0, 0.45, D / 2 - 0.05, d.wood);
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) p.box(0.1, z < 0 ? 2.3 : 2.0, 0.1, x * (W / 2 - 0.05), z < 0 ? 1.15 : 1.0, z * (D / 2 - 0.05), d.wood);
  const strips = c.hi ? 6 : 2;
  const ang = Math.atan2(0.3, D + 0.2);
  for (let i = 0; i < strips; i++) {
    const sw = (W + 0.3) / strips;
    p.box(sw, 0.05, Math.hypot(D + 0.4, 0.3), -(W + 0.3) / 2 + sw * (i + 0.5), 2.2, 0.1, d.awning[i % d.awning.length]!, {}, ang);
  }
  const goods = c.hi ? 6 : 2;
  for (let i = 0; i < goods; i++) {
    const s = 0.22 + c.d() * 0.12;
    p.box(s, s * 0.8, s, -W / 2 + 0.3 + (i + 0.5) * ((W - 0.6) / goods), 0.94 + s * 0.4, (c.d() - 0.5) * (D - 0.4), c.dpick(d.goods), { tint: 0.08 });
  }
  return { colliders: [cuboid(W / 2, 0.5, D / 2, 0, 0.5, 0, "wood")] };
}

function lantern(c: Ctx, d: LanternDef): Built {
  const p = c.parts;
  const H = d.height;
  p.box(0.12, H, 0.12, 0, H / 2, 0, d.post);
  p.box(0.4, 0.08, 0.08, 0.16, H - 0.1, 0, d.post);
  p.box(0.22, 0.3, 0.22, 0.3, H - 0.32, 0, d.glass, { lit: 1, tint: 0.02 });
  if (c.hi) {
    p.box(0.28, 0.06, 0.28, 0.3, H - 0.15, 0, d.post);
    p.box(0.26, 0.04, 0.26, 0.3, H - 0.49, 0, d.post);
    p.box(0.3, 0.1, 0.3, 0, 0.05, 0, d.post);
  }
  return { colliders: [cylinder(0.09, H / 2, 0, H / 2, 0, "wood")] };
}

function prop(c: Ctx, d: PropDef): Built {
  return { colliders: furniture(c, { kind: d.kind, at: [0, 0], colour: d.colour, ...(d.band ? { accent: d.band } : {}) }, { wall: d.colour, wood: d.colour, stone: d.colour, roof: d.colour }) };
}

const BUILDERS: { [K in PieceDef["kind"]]: (c: Ctx, d: Extract<PieceDef, { kind: K }>) => Built } = {
  house,
  ruin,
  bridge,
  fence,
  well,
  stall,
  lantern,
  crate: prop,
  barrel: prop,
};

// ── placing, cutting, batching, checking ───────────────────────────────────────────────────────────

/** Where a piece stands: position and yaw (three.js's rotation.y). */
export interface KitPlacement {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** A point from the piece's frame to the world. */
export function toWorld(place: KitPlacement, x: number, y: number, z: number): KitVec3 {
  const c = Math.cos(place.yaw);
  const s = Math.sin(place.yaw);
  return { x: place.x + x * c + z * s, y: place.y + y, z: place.z - x * s + z * c };
}

/** A point from the world to the piece's frame. */
export function toLocal(place: KitPlacement, x: number, y: number, z: number): KitVec3 {
  const dx = x - place.x;
  const dz = z - place.z;
  const c = Math.cos(place.yaw);
  const s = Math.sin(place.yaw);
  return { x: dx * c - dz * s, y: y - place.y, z: dx * s + dz * c };
}

/** The yaw as a quaternion (a body's rotation for a placed piece's colliders). */
export function yawRotation(yaw: number): KitQuat {
  return yawQuat(yaw);
}

/** An interior placed in the world: its room as an oriented box (centre, half extents, yaw) and its door. */
export function placeInterior(i: InteriorDesc, place: KitPlacement): { bounds: { x: number; y: number; z: number; hx: number; hy: number; hz: number; yaw: number }; door: KitVec3 & { nx: number; nz: number } } {
  const b = i.bounds;
  const centre = toWorld(place, (b.min.x + b.max.x) / 2, (b.min.y + b.max.y) / 2, (b.min.z + b.max.z) / 2);
  const door = toWorld(place, i.door.x, i.door.y, i.door.z);
  const c = Math.cos(place.yaw);
  const s = Math.sin(place.yaw);
  return {
    bounds: { ...centre, hx: (b.max.x - b.min.x) / 2, hy: (b.max.y - b.min.y) / 2, hz: (b.max.z - b.min.z) / 2, yaw: place.yaw },
    door: { ...door, nx: i.door.nx * c + i.door.nz * s, nz: -i.door.nx * s + i.door.nz * c },
  };
}

/**
 * The cut-away mask for a building seen from `eye` (world): the roof always, and every wall whose outer face looks
 * toward the eye (the eye is outside its plane by more than `margin` m). Bit k set cuts group k.
 */
export function cutMask(i: InteriorDesc, place: KitPlacement, eye: { x: number; z: number }, margin = -0.25): number {
  const e = toLocal(place, eye.x, 0, eye.z);
  let mask = 1 << CUT_GROUPS.roof;
  for (const w of i.walls) if (w.nx * e.x + w.nz * e.z > w.d + margin) mask |= 1 << w.group;
  return mask;
}

/** One piece to batch: where it stands, and its owner number for the cut-away (default its index + 1). */
export interface BatchItem {
  piece: KitPiece;
  place: KitPlacement;
  owner?: number;
}

/**
 * Merge placed pieces into one geometry (one draw call): each piece's near form (or its far form with `far`) moved
 * into place, its cut attribute rewritten to its owner number. Returns the geometry and each item's owner.
 */
export function batchPieces(items: readonly BatchItem[], o: { far?: boolean } = {}): { geometry: THREE.BufferGeometry; owners: number[]; triangles: number } {
  const parts: THREE.BufferGeometry[] = [];
  const owners: number[] = [];
  const m = new THREE.Matrix4();
  items.forEach((it, i) => {
    const src = o.far ? (it.piece.lods?.[0]?.geometry ?? it.piece.geometry) : it.piece.geometry;
    const owner = it.owner ?? i + 1;
    owners.push(owner);
    if (src.getAttribute("position").count === 0) return;
    const g = src.clone();
    const cut = g.getAttribute(CUT_ATTRIBUTE) as THREE.BufferAttribute;
    for (let k = 0; k < cut.count; k++) cut.setX(k, owner * CUT_OWNER_STRIDE + (cut.getX(k) % CUT_OWNER_STRIDE));
    g.applyMatrix4(m.makeRotationY(it.place.yaw).setPosition(it.place.x, it.place.y, it.place.z));
    parts.push(g);
  });
  if (parts.length === 0) return { geometry: new THREE.BufferGeometry(), owners, triangles: 0 };
  const geometry = mergeKit(parts);
  return { geometry, owners, triangles: geometry.getAttribute("position").count / 3 };
}

function mergeKit(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  // every kit geometry has the same six attributes, so a straight concatenation merges them
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(parts[0]!.attributes)) {
    const size = parts[0]!.getAttribute(name).itemSize;
    const total = parts.reduce((n, g) => n + g.getAttribute(name).count, 0);
    const arr = new Float32Array(total * size);
    let off = 0;
    for (const g of parts) {
      const a = g.getAttribute(name);
      arr.set(a.array as Float32Array, off);
      off += a.count * size;
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, size));
  }
  for (const g of parts) g.dispose();
  out.computeBoundingSphere();
  return out;
}

/** What is wrong with a built piece's colliders and interior (empty when fine): every number finite, every size
 *  positive, every collider inside the piece's mesh bounds (grown by `slack` m), at least one collider, and the
 *  interior's door on its room's edge. */
export function checkPiece(piece: KitPiece, slack = 0.3): string[] {
  const out: string[] = [];
  const g = piece.geometry;
  g.computeBoundingBox();
  const bb = g.boundingBox!.clone().expandByScalar(slack);
  if (piece.colliders.length === 0) out.push("no colliders");
  piece.colliders.forEach((c, i) => {
    const nums = [c.offset.x, c.offset.y, c.offset.z, ...(c.rotation ? [c.rotation.x, c.rotation.y, c.rotation.z, c.rotation.w] : [])];
    const s = c.shape;
    const sizes = s.type === "cuboid" ? [s.halfExtents.x, s.halfExtents.y, s.halfExtents.z] : s.type === "cylinder" ? [s.halfHeight, s.radius] : [s.radius];
    if (![...nums, ...sizes].every(Number.isFinite)) out.push(`collider ${i}: not finite`);
    if (sizes.some((v) => v <= 0)) out.push(`collider ${i}: a size is not > 0`);
    if (!bb.containsPoint(new THREE.Vector3(c.offset.x, c.offset.y, c.offset.z))) out.push(`collider ${i}: centre outside the piece's bounds`);
    if (c.rotation && Math.abs(Math.hypot(c.rotation.x, c.rotation.y, c.rotation.z, c.rotation.w) - 1) > 1e-6) out.push(`collider ${i}: rotation not unit`);
  });
  if (piece.interior) {
    const b = piece.interior.bounds;
    if (!(b.max.x > b.min.x && b.max.y > b.min.y && b.max.z > b.min.z)) out.push("interior: empty room");
    const dr = piece.interior.door;
    const along = dr.nx * dr.x + dr.nz * dr.z;
    const edge = dr.nx !== 0 ? b.max.x * Math.sign(dr.nx) : b.max.z * Math.sign(dr.nz);
    if (along < edge - 1e-6 || along > edge + 0.5) out.push("interior: the door is not on the room's edge");
  }
  return out;
}

/** A hex hash of a geometry's positions and colours (rounded to 0.1 mm and 1/4096), for determinism checks. */
export function hashGeometry(g: THREE.BufferGeometry): string {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v | 0;
    h = Math.imul(h, 0x01000193);
  };
  for (const [name, k] of [
    ["position", 1e4],
    ["color", 4096],
    [CUT_ATTRIBUTE, 1],
  ] as const) {
    const a = g.getAttribute(name);
    if (!a) continue;
    const arr = a.array as ArrayLike<number>;
    for (let i = 0; i < arr.length; i++) mix(Math.round(arr[i]! * k));
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export { type KitColour };
